import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { Form, Modal, Spin } from 'antd';
import { getCodigosPostales, getTiposDeVialidades } from 'api/catalogos';
import _logger from 'utils/logger';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import FormItemsDireccion from 'components/Direccion/FormItemsDireccion';

export const formatAddressValues = (dir) => {
  const values = {};
  Object.keys(dir).forEach((key) => {
    const val = dir[key];
    values[key] = val && val !== 'N/A' ? val : undefined;
  });
  return values;
};

const Direccion = ({
  isEditing,
  propName,
  fatherObject,
  setLoading,
  altPropName,
  modal,
  onCancel: _onCancel,
  callback,
  visible,
  loading,
  patchParent,
}) => {
  const formInstance = Form.useFormInstance();
  const [_form] = Form.useForm();
  const [colonias, setColonias] = useState([]);
  const [vialidades, setVialidades] = useState([]);
  const form = modal ? _form : formInstance;

  const onSearchCodigoPostal = async (d_codigo, validate = true) => {
    try {
      if (!d_codigo) return;
      setLoading(true);
      const _colonias = await getCodigosPostales({ d_codigo });
      const municipio = _colonias[0]?.municipio.nom_mun;
      const estado = _colonias[0]?.municipio.estado.nombre_de_AGEE;
      setColonias(_colonias);
      const values = {
        municipio,
        estado,
        pais: 'México',
      };
      if (_colonias.length === 1) {
        values.colonia = _colonias[0].id;
      } else if (validate) {
        form.resetFields(['colonia']);
      }
      setTimeout(() => {
        form.setFieldsValue(values);
      });
    } catch (error) {
      _logger(error);
    } finally {
      setLoading(false);
    }
  };

  const setDireccion = async (_propName = propName) => {
    if (fatherObject?.[_propName]) {
      const direction = formatAddressValues(fatherObject[_propName]);
      const municipio = direction.codigo_postal?.municipio?.nom_mun;
      const codigo_postal = direction.codigo_postal?.d_codigo;
      await onSearchCodigoPostal(codigo_postal, false);
      form.setFieldsValue({
        ...direction,
        municipio,
        codigo_postal,
        colonia: direction.codigo_postal.id,
        pais: direction.codigo_postal?.municipio?.estado?.pais?.descripcion,
      });
    } else {
      form.resetFields();
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      const _vialidades = await getTiposDeVialidades();
      if (isMounted) {
        setVialidades(_vialidades);
        setDireccion();
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setDireccion();
  }, [fatherObject]);

  const onCancel = () => {
    _onCancel();
    setColonias([]);
  };

  const rules = {
    required: [
      {
        required: true,
      },
    ],
    codigo_postal: [
      {
        required: true,
        message: 'El código postal es requerido',
      },
      {
        pattern: /^[0-9]{5}$/,
        message: 'El código postal debe ser de 5 dígitos',
      },
    ],
  };

  const onClickCopy = () => setDireccion(altPropName);

  const onFinish = async (_values) => {
    const values = { ..._values };
    values.codigo_postal = values.colonia;
    delete values.colonia;
    setLoading(true);
    const saved = await patchParent(fatherObject.id, { [propName]: values });
    if (saved) callback();
    setLoading(false);
  };

  if (modal) {
    return (
      <Modal
        width="90%"
        visible={visible}
        closable={false}
        maskClosable={false}
        keyboard={!loading}
        footer={null}
        title={(
          <FormSubmitCustomControls
            label="Dirección"
            onFinish={isEditing ? form.submit : null}
            onCancel={onCancel}
            loading={loading}
          />
        )}
        onCancel={onCancel}
        forceRender
        centered
      >
        <Spin tip="Cargando..." spinning={loading} key={fatherObject?.id}>
          <Form
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            form={_form}
          >
            <FormItemsDireccion
              isEditing={isEditing}
              onSearchCodigoPostal={onSearchCodigoPostal}
              rules={rules}
              vialidades={vialidades}
              colonias={colonias}
              onClickCopy={altPropName && isEditing ? onClickCopy : null}
            />
          </Form>
        </Spin>
      </Modal>
    );
  }

  return (
    <FormItemsDireccion
      isEditing={isEditing}
      onSearchCodigoPostal={onSearchCodigoPostal}
      rules={rules}
      vialidades={vialidades}
      colonias={colonias}
      onClickCopy={altPropName && isEditing ? onClickCopy : null}
    />
  );
};

Direccion.propTypes = {
  isEditing: propTypes.bool,
  propName: propTypes.string.isRequired,
  altPropName: propTypes.string,
  fatherObject: propTypes.shape(),
  setLoading: propTypes.func.isRequired,
  modal: propTypes.bool,
  visible: propTypes.bool,
  loading: propTypes.bool,
  onCancel: propTypes.func,
  patchParent: propTypes.func,
  callback: propTypes.func,
};

Direccion.defaultProps = {
  isEditing: false,
  altPropName: null,
  modal: false,
  visible: false,
  loading: false,
  onCancel: null,
  patchParent: null,
  callback: null,
  fatherObject: null,
};

export default Direccion;
